"use client";

import { c as _c } from "react/compiler-runtime";
import { IconCryptoWallet } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconAlertInfoOutline18, IconArrowRightOutline18, IconArrowTrendUpOutline18, IconCircleCheckOutline18, IconClockOutline18, IconDotsOutline18, IconEarthOutline18, IconXmarkOutline18 } from "nucleo-ui-outline-18";
import { useChainInfoMap } from "@/app/(v2)/workflows/_hooks/useChainInfo";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useChainSearch } from "@/hooks/useChainSearch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { TokenWithChainIcon } from "@/components/ui/token-with-chain-icon";
import { useFilteredTokens } from "@/hooks/useFilteredTokens";
import { useDebouncedRelayTokenSearch } from "@/hooks/useRelayTokenSearch";
import { tokenInfoKey, useSimBalance, useSimTokenInfo } from "@/hooks/useSimTokenInfo";
import { formatAddress, formatUsdValue } from "@/lib/utils";
import { isNativeToken, isNativeTokenAddress } from "@/lib/tokens/nativeToken";
import { shouldShowUnverifiedCommitButton } from "@/lib/tokens/shouldShowUnverifiedCommitButton";
import { addressEquals, normalizeAddress } from "@/lib/utils/address";
import { addRecentToken, getRecentTokens, type RecentToken } from "@/lib/utils/recentSelections";
import { useModalStore } from "@b3dotfun/sdk/global-account/react";
import { chainById } from "@b3/chain-registry";
import { useCallback, useEffect, useMemo, useState } from "react";
import { formatUnits, getAddress, isAddress } from "viem";
import type { TokenSelectorProps } from "./TokenSelector";
function isEvmChainId(id: number | undefined): boolean {
  if (!id) return false;
  return chainById.get(id)?.type === "evm";
}
type SelectorDisplayToken = {
  chainId: number;
  address: string;
  symbol: string;
  name: string;
  metadata: {
    logoURI?: string | null;
    verified: boolean;
    isNative: boolean;
  };
};
function TokenSelectorIcon(t0) {
  const $ = _c(14);
  const {
    token,
    size: t1
  } = t0;
  const size = t1 === undefined ? 32 : t1;
  const chainBadgeSize = size <= 20 ? 10 : 14;
  let t2;
  if ($[0] !== token.chainId) {
    t2 = [token.chainId];
    $[0] = token.chainId;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    data: chainInfoMap
  } = useChainInfoMap(t2);
  let t3;
  if ($[2] !== chainInfoMap || $[3] !== token.chainId) {
    t3 = chainInfoMap.get(token.chainId);
    $[2] = chainInfoMap;
    $[3] = token.chainId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const chainInfo = t3;
  const t4 = chainInfo?.iconUrl;
  const t5 = chainInfo?.name;
  let t6;
  if ($[5] !== chainBadgeSize || $[6] !== size || $[7] !== t4 || $[8] !== t5 || $[9] !== token.address || $[10] !== token.chainId || $[11] !== token.metadata.logoURI || $[12] !== token.symbol) {
    t6 = <TokenWithChainIcon logoUrl={token.metadata.logoURI} symbol={token.symbol} chainId={token.chainId} tokenAddress={token.address} tokenSize={size} chainIconUrl={t4} chainName={t5} chainIconSize={chainBadgeSize} />;
    $[5] = chainBadgeSize;
    $[6] = size;
    $[7] = t4;
    $[8] = t5;
    $[9] = token.address;
    $[10] = token.chainId;
    $[11] = token.metadata.logoURI;
    $[12] = token.symbol;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function TokenSelectorTitle(t0) {
  const $ = _c(5);
  const {
    token,
    className: t1
  } = t0;
  const className = t1 === undefined ? "body-sm-semibold truncate" : t1;
  let t2;
  if ($[0] !== token.symbol) {
    t2 = <span className="font-semibold text-zinc-950 dark:text-white">{token.symbol}</span>;
    $[0] = token.symbol;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== className || $[3] !== t2) {
    t3 = <div className={className}>{t2}</div>;
    $[2] = className;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
type TokenBalanceInfo = {
  balance?: string | null;
  decimals: number;
  symbol: string;
  value_usd?: number | null;
};
function TokenBalanceRow(t0) {
  const $ = _c(15);
  const {
    token
  } = t0;
  if (!token.balance) {
    return null;
  }
  let humanBalance;
  try {
    let t1;
    if ($[0] !== token.balance || $[1] !== token.decimals) {
      t1 = formatUnits(BigInt(token.balance), token.decimals);
      $[0] = token.balance;
      $[1] = token.decimals;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    humanBalance = t1;
  } catch {
    return null;
  }
  let t1;
  if ($[3] !== token.value_usd) {
    t1 = formatUsdValue(token.value_usd);
    $[3] = token.value_usd;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const formattedUsd = t1;
  const t2 = parseFloat(humanBalance);
  let t3;
  if ($[5] !== t2) {
    t3 = t2.toLocaleString(undefined, {
      maximumFractionDigits: 6
    });
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3 || $[8] !== token.symbol) {
    t4 = <p className="truncate text-xs font-medium text-blue-600 dark:text-blue-400">Balance: {t3} {token.symbol}</p>;
    $[7] = t3;
    $[8] = token.symbol;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== formattedUsd) {
    t5 = formattedUsd && <p className="text-ds-text-muted flex-shrink-0 text-xs">{formattedUsd}</p>;
    $[10] = formattedUsd;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div className="flex items-center justify-between gap-2">{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}

/**
 * Relay + SimDune token picker — the default `TokenSelector` behavior. Extracted into
 * its own component so hooks are unconditional within each branch, satisfying React's
 * Rules of Hooks. The outer `TokenSelector` dispatcher picks between this and
 * `GenericPickerMode` without calling any hooks itself.
 */
function TokenSelectorRelayMode(props: TokenSelectorProps) {
  const {
    label,
    description,
    headerRight,
    value,
    onChange,
    onChainIdChange,
    placeholder = "Search token or enter 0x address...",
    required = false,
    chainId,
    chainIds,
    workflowWalletAddress,
    showAnySpendFundingCta = false,
    multiple = false,
    popoverModal = false,
    allowedChainIds,
    showChainFilter = true,
    excludeNative = false,
    evmOnly = false
  } = props;
  const [open, setOpen] = useState(false);
  const [isTouched, setTouched] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // --- Built-in chain filter state ---
  // chainFilterOpen drives the useChainSearch hook — active when main popover is open
  const chainFilterOpen = open && showChainFilter;
  const [selectedFilterChainId, setSelectedFilterChainId] = useState<number | undefined>(() => {
    // Initialize from external chainId prop if available
    if (showChainFilter && chainId && typeof chainId === "number" && chainId > 0) return chainId;
    return undefined;
  });
  const [recentTokens, setRecentTokens] = useState<RecentToken[]>(() => getRecentTokens());
  // Cache resolved tokens by normalized address so chips keep their symbol/name
  // when Relay refetches drop them from `tokens` — e.g. user types a search
  // query that filters out previously-selected tokens, or chain changes.
  // Single mode reads exactly one entry; multi mode accumulates one per chip.
  const [cachedSelectedTokens, setCachedSelectedTokens] = useState<Record<string, SelectorDisplayToken>>({});
  // Sync filter chain when external chainId prop changes (e.g., variable resolution)
  useEffect(() => {
    if (showChainFilter && chainId && typeof chainId === "number" && chainId > 0) {
      setSelectedFilterChainId(chainId);
    }
  }, [chainId, showChainFilter]);

  // Chain filter expanded state
  const [chainFilterExpanded, setChainFilterExpanded] = useState(false);
  const refreshRecentTokens = useCallback(() => {
    setRecentTokens(getRecentTokens());
  }, []);

  // Chain search for the built-in filter dropdown
  const {
    orderedChains: allChains,
    recentChains: filterRecentChains,
    handleSearchChange: handleChainSearchChange,
    searchQuery: chainSearchQuery
  } = useChainSearch({
    allowedChainIds,
    open: chainFilterOpen
  });

  // Effective chain ID: built-in filter takes precedence when shown, then external prop
  // When showChainFilter is on, the user controls chain via the dropdown — ignore external chainId
  const effectiveChainId = showChainFilter ? selectedFilterChainId : chainId;

  // Normalize value for multi-select mode
  const selectedAddresses = useMemo(() => {
    if (multiple) {
      return Array.isArray(value) ? value : value ? [value] : [];
    }
    return [];
  }, [value, multiple]);

  // For single mode, get the string value
  const singleValue: string = multiple ? "" : typeof value === "string" ? value : "";

  // AnySpend modal hooks
  const setModalOpen = useModalStore(state => state.setB3ModalOpen);
  const setModalContentType = useModalStore(state_0 => state_0.setB3ModalContentType);

  // Convert chainId to number if it's a string (undefined if no chain selected)
  const numericChainId = effectiveChainId ? typeof effectiveChainId === "string" ? parseInt(effectiveChainId as string, 10) : effectiveChainId : undefined;
  const hasValidChainId = !!(numericChainId && numericChainId > 0 && !isNaN(numericChainId));

  // Convert chainIds array to numbers (filter out invalid values)
  const numericChainIds = useMemo(() => {
    if (!chainIds || chainIds.length === 0) return null;
    return chainIds.map(id => typeof id === "string" ? parseInt(id, 10) : id).filter(id_0 => !isNaN(id_0));
  }, [chainIds]);
  const hasValidChainIds = numericChainIds && numericChainIds.length > 0;

  // Fetch balances only for the selected workflow (Turnkey) wallet
  const balanceChainIds = hasValidChainIds ? numericChainIds : hasValidChainId ? [numericChainId] : undefined;
  const {
    data: workflowWalletBalancesRaw
  } = useSimBalance(workflowWalletAddress, balanceChainIds, !!workflowWalletAddress);

  // Filter out spam/low-liquidity tokens from workflow wallet balances
  const {
    filtered: filteredWalletBalances
  } = useFilteredTokens(workflowWalletBalancesRaw?.balances);
  const workflowWalletBalances = useMemo(() => workflowWalletBalancesRaw ? {
    ...workflowWalletBalancesRaw,
    balances: filteredWalletBalances
  } : undefined, [workflowWalletBalancesRaw, filteredWalletBalances]);

  // Pass chainId only if valid, otherwise undefined (will search all chains)
  const {
    tokens,
    isLoading: isLoadingRelay,
    searchTerm,
    setSearchTerm
  } = useDebouncedRelayTokenSearch(hasValidChainId ? numericChainId : undefined, 50);

  // Resolve the "lookup term" — the address we're currently verifying against
  // the chain registry. While the user types/pastes, that's `searchTerm`. When
  // idle with a committed selection, that's `singleValue`. Decoupling this
  // from `singleValue` alone is required by the paste-commit rework: a typed
  // address must drive the chain-scoped lookup even though nothing is
  // committed yet (commit only happens on explicit click).
  const lookupTerm = (searchTerm?.trim() || singleValue || "").trim();
  // Non-strict isAddress: EVM addresses are case-insensitive at the protocol
  // level, so accept any case here. The mixed-case EIP-55 checksum is a
  // typo-protection layer, not identity — and we already accept all-lowercase
  // input, so rejecting mixed-case-with-bad-checksum would be inconsistent.
  const isValidAddress = !!lookupTerm && lookupTerm.startsWith("0x") && lookupTerm.length === 42 && isAddress(lookupTerm, {
    strict: false
  });

  // Fetch token info from SimDune when a valid address is entered (for custom tokens not in Relay)
  const {
    data: simTokenData,
    isLoading: isLoadingSimToken
  } = useSimTokenInfo(isValidAddress ? lookupTerm : undefined, hasValidChainId ? numericChainId : undefined, !!(isValidAddress && hasValidChainId));

  // Combine loading states
  const isLoading = isLoadingRelay || isLoadingSimToken;

  // Convert SimDune token to Relay format if available
  const simToken = useMemo(() => {
    if (simTokenData && simTokenData.tokens.length > 0) {
      const token = simTokenData.tokens[0];
      return {
        chainId: token.chain_id,
        address: simTokenData.contract_address,
        symbol: token.symbol,
        name: token.name || token.symbol,
        decimals: token.decimals,
        vmType: "evm",
        metadata: {
          logoURI: token.logo,
          verified: true,
          // SimDune tokens are generally verified
          isNative: isNativeTokenAddress(simTokenData.contract_address)
        }
      };
    }
    return null;
  }, [simTokenData]);

  // Merge Relay tokens with SimDune token (if it's a direct address lookup)
  const allTokens = useMemo(() => {
    // If we have a SimDune token for this address, use it
    if (simToken && isValidAddress) {
      // Check if this token is already in Relay results
      const existsInRelay = tokens.some(t => addressEquals(t.address, lookupTerm));
      if (!existsInRelay) {
        return [simToken, ...tokens];
      }
    }
    return tokens;
  }, [simToken, tokens, isValidAddress, lookupTerm]);

  // Extract unique chain IDs from tokens for fetching chain info
  const tokenChainIds = useMemo(() => {
    const ids = new Set(allTokens.map(t_0 => t_0.chainId));
    // Include recent token chain IDs and the selected filter chain so names resolve
    for (const rt of recentTokens) ids.add(rt.chainId);
    if (selectedFilterChainId) ids.add(selectedFilterChainId);
    return [...ids];
  }, [allTokens, recentTokens, selectedFilterChainId]);

  // Fetch chain information for all tokens
  const {
    data: chainInfoMap
  } = useChainInfoMap(tokenChainIds);

  // Build chain filter options from the full chain list (recent first, then popular)
  const chainFilterOptions = useMemo(() => {
    // Start with recent chains, then fill with top chains from the full list
    const seen = new Set<number>();
    const options: {
      chainId: number;
      name: string;
      iconUrl: string;
    }[] = [];
    for (const rc of filterRecentChains) {
      if (evmOnly && !isEvmChainId(rc.chainId)) continue;
      if (rc.iconUrl && !seen.has(rc.chainId)) {
        seen.add(rc.chainId);
        options.push({
          chainId: rc.chainId,
          name: rc.name,
          iconUrl: rc.iconUrl
        });
      }
    }
    for (const chain of allChains) {
      if (evmOnly && !isEvmChainId(chain.chainId)) continue;
      if (chain.iconUrl && !chain.isTestnet && !seen.has(chain.chainId)) {
        seen.add(chain.chainId);
        options.push({
          chainId: chain.chainId,
          name: chain.name,
          iconUrl: chain.iconUrl
        });
      }
      if (options.length >= 8) break;
    }
    return options;
  }, [allChains, filterRecentChains, evmOnly]);

  // Convert balance items to token format for display
  const workflowWalletTokens = useMemo(() => {
    if (!workflowWalletBalances?.balances) return [];
    return workflowWalletBalances.balances.map(balance => ({
      chainId: balance.chain_id,
      address: balance.address,
      symbol: balance.symbol,
      name: balance.name || balance.symbol,
      decimals: balance.decimals,
      vmType: "evm" as const,
      metadata: {
        logoURI: balance.token_metadata?.logo,
        verified: true,
        isNative: isNativeTokenAddress(balance.address)
      },
      balance: balance.amount,
      value_usd: balance.value_usd
    }));
  }, [workflowWalletBalances]);
  const walletTokenByKey = useMemo(() => {
    const m = new Map<string, (typeof workflowWalletTokens)[number]>();
    for (const t_1 of workflowWalletTokens) m.set(tokenInfoKey(t_1.address, t_1.chainId), t_1);
    return m;
  }, [workflowWalletTokens]);

  // Pre-warm `cachedSelectedTokens` whenever any source — Relay results, the
  // workflow wallet's balances, or the user's recent-tokens history — has
  // metadata for a currently-selected address that we haven't cached yet. This
  // closes three gaps the commit-time cache (in `handleSelectToken` /
  // `handleUseUnverifiedAddress`) doesn't cover:
  //   1. Hydrated-on-mount values (saved workflow loaded with addresses the
  //      user never selected this session).
  //   2. Recent-token clicks where the recent token isn't in the current
  //      Relay scope (e.g. niche token, chain filter just changed) so
  //      `handleSelectToken`'s `tokenMeta` lookup misses and the cache write
  //      is skipped.
  //   3. Filter chain changes that drop a chip's chain from `allTokens`.
  // Without this, typing in the search input shrinks `allTokens` further and
  // the chip falls through to the synthesize-address-ellipsis fallback.
  useEffect(() => {
    const targets = multiple ? selectedAddresses : singleValue ? [singleValue] : [];
    if (targets.length === 0) return;
    setCachedSelectedTokens(prev => {
      let next = prev;
      const ensureNext = () => {
        if (next === prev) next = {
          ...prev
        };
        return next;
      };
      for (const addr of targets) {
        const key = normalizeAddress(addr);
        if (next[key]) continue;
        const fromAll = allTokens.find(t_2 => addressEquals(t_2.address, addr));
        if (fromAll) {
          ensureNext()[key] = {
            chainId: fromAll.chainId,
            address: fromAll.address,
            symbol: fromAll.symbol,
            name: fromAll.name,
            metadata: fromAll.metadata
          };
          continue;
        }
        const fromWallet = workflowWalletTokens.find(t_3 => addressEquals(t_3.address, addr));
        if (fromWallet) {
          ensureNext()[key] = {
            chainId: fromWallet.chainId,
            address: fromWallet.address,
            symbol: fromWallet.symbol,
            name: fromWallet.name,
            metadata: fromWallet.metadata
          };
          continue;
        }
        const fromRecent = recentTokens.find(rt_0 => addressEquals(rt_0.address, addr));
        if (fromRecent) {
          ensureNext()[key] = {
            chainId: fromRecent.chainId,
            address: fromRecent.address,
            symbol: fromRecent.symbol,
            name: fromRecent.name,
            metadata: {
              logoURI: fromRecent.logoURI,
              verified: true,
              isNative: false
            }
          };
        }
      }
      return next;
    });
  }, [multiple, selectedAddresses, singleValue, allTokens, workflowWalletTokens, recentTokens]);

  // Final fallback for single mode: if the committed value is a valid address
  // but no indexer (Relay, SimDune, wallet balances, cache) resolves it to a
  // known token, synthesize a minimal "unverified" display token so the chip
  // still renders. Without this fallback, forms holding an unverified address
  // (either freshly committed via "Use this address anyway" or hydrated from a
  // previously-saved value) would show an empty input despite a set form
  // value — confusing, and visually hides form state from the user.
  const synthesizedUnverifiedToken: SelectorDisplayToken | null = useMemo(() => {
    if (multiple) return null;
    // Non-strict: any case accepted, the chip exists purely to make the
    // committed form value visible regardless of the checksum pattern.
    if (!singleValue || !isAddress(singleValue, {
      strict: false
    })) return null;
    if (!numericChainId) return null;
    if (isLoading) return null;
    if (allTokens.some(t_4 => addressEquals(t_4.address, singleValue))) return null;
    if (workflowWalletTokens.some(t_5 => addressEquals(t_5.address, singleValue))) return null;
    if (simToken && addressEquals(simToken.address, singleValue)) return null;
    if (cachedSelectedTokens[normalizeAddress(singleValue)]) return null;
    // Lowercase before checksum-normalizing so a committed bad-checksum value
    // from an older save doesn't throw on hydration.
    let checksum = singleValue;
    try {
      checksum = getAddress(singleValue.toLowerCase());
    } catch {
      // keep raw if normalization somehow fails
    }
    return {
      chainId: numericChainId,
      address: checksum,
      symbol: `${checksum.slice(0, 6)}…${checksum.slice(-4)}`,
      name: "Unverified token",
      metadata: {
        logoURI: null,
        verified: false,
        isNative: false
      }
    };
  }, [multiple, singleValue, numericChainId, isLoading, allTokens, workflowWalletTokens, simToken, cachedSelectedTokens]);

  // Find the selected token from the list (check Relay, SimDune, and wallet balances) - for single mode.
  // simToken is derived from lookupTerm (which prefers searchTerm over singleValue), so it can
  // resolve to a different address than the committed form value if the user typed but dismissed
  // without committing. Guard on addressEquals so the chip never displays a mismatched token.
  const selectedToken = !multiple ? allTokens.find(t_7 => addressEquals(t_7.address, singleValue)) || workflowWalletTokens.find(t_6 => addressEquals(t_6.address, singleValue)) || (simToken && addressEquals(simToken.address, singleValue) ? simToken : null) || cachedSelectedTokens[normalizeAddress(singleValue)] || synthesizedUnverifiedToken : null;
  const anySpendActionLabel = selectedToken ? `Swap or deposit ${selectedToken.symbol}` : "";

  // For multiple mode, find all selected tokens. If a committed address isn't
  // in either indexer list (e.g. user clicked "Use it anyway" on an address
  // Relay doesn't know, or the form hydrated from a saved value), synthesize a
  // minimal display token so the chip still renders — otherwise the form holds
  // the address but the UI shows nothing, and the user can't remove it.
  const selectedTokens = useMemo(() => {
    if (!multiple) return [];
    return selectedAddresses.map(addr_0 => {
      const found = allTokens.find(t_9 => addressEquals(t_9.address, addr_0)) || workflowWalletTokens.find(t_8 => addressEquals(t_8.address, addr_0)) || cachedSelectedTokens[normalizeAddress(addr_0)];
      if (found) return found;
      if (!isAddress(addr_0, {
        strict: false
      }) || !numericChainId) return undefined;
      let checksum_0 = addr_0;
      try {
        checksum_0 = getAddress(addr_0.toLowerCase());
      } catch {
        // keep raw if normalization somehow fails
      }
      return {
        chainId: numericChainId,
        address: checksum_0,
        symbol: `${checksum_0.slice(0, 6)}…${checksum_0.slice(-4)}`,
        name: "Unverified token",
        metadata: {
          logoURI: null,
          verified: false,
          isNative: false
        }
      } satisfies SelectorDisplayToken;
    }).filter(Boolean) as typeof allTokens;
  }, [multiple, selectedAddresses, allTokens, workflowWalletTokens, numericChainId, cachedSelectedTokens]);
  const showError = isTouched && (multiple ? selectedAddresses.length === 0 : !singleValue);
  const handleInputChange = (newValue: string) => {
    // Typing NEVER commits — the committed value only changes when the user
    // clicks a rendered result (known token) or the "Use this address anyway"
    // button. This keeps both single and multi mode on the same contract:
    // the input is a transient search term; the form value is stable.
    setSearchTerm(newValue);

    // Keep the dropdown open while the user has content in the input — even
    // for full 42-char addresses, because that's where a resolved token (or
    // the "Use it anyway" escape hatch) will appear.
    if (newValue) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };
  const handleSelectToken = (tokenAddress: string, tokenChainId: number) => {
    // Convert to checksum address
    let checksumAddress = tokenAddress;
    try {
      if (isAddress(tokenAddress)) {
        checksumAddress = getAddress(tokenAddress);
      }
    } catch (error) {
      console.warn("[TokenSelector] Failed to convert to checksum:", error);
    }

    // Find the token metadata for recent history + caching
    const tokenMeta = allTokens.find(t_11 => addressEquals(t_11.address, tokenAddress) && t_11.chainId === tokenChainId) || workflowWalletTokens.find(t_10 => addressEquals(t_10.address, tokenAddress) && t_10.chainId === tokenChainId);
    if (tokenMeta) {
      // Cache the token so chips keep their symbol/name when allTokens refetches
      // (e.g. user types a search query that filters this address out of the list).
      setCachedSelectedTokens(prev_0 => ({
        ...prev_0,
        [normalizeAddress(checksumAddress)]: {
          chainId: tokenMeta.chainId,
          address: checksumAddress,
          symbol: tokenMeta.symbol,
          name: tokenMeta.name,
          metadata: tokenMeta.metadata
        }
      }));
      addRecentToken({
        address: checksumAddress,
        chainId: tokenChainId,
        symbol: tokenMeta.symbol,
        name: tokenMeta.name,
        logoURI: tokenMeta.metadata.logoURI
      });
      refreshRecentTokens();
    }
    if (multiple) {
      // In multiple mode, add to the array (if not already selected). Use
      // addressEquals so hydrated lowercase/bad-checksum saved values don't
      // duplicate when re-selected via a different commit path.
      if (!selectedAddresses.some(a => addressEquals(a, checksumAddress))) {
        onChange([...selectedAddresses, checksumAddress]);
      }
      setSearchTerm("");
      // Keep popover open for more selections
    } else {
      onChange(checksumAddress);
      setSearchTerm("");
      setOpen(false);
      setIsEditing(false);
    }

    // Auto-set chain based on selected token
    if (onChainIdChange) {
      onChainIdChange(tokenChainId);
    }
    // Also update built-in filter to match
    if (showChainFilter) {
      setSelectedFilterChainId(tokenChainId);
    }
  };
  const handleRemoveToken = (addressToRemove: string) => {
    if (multiple) {
      onChange(selectedAddresses.filter(addr_1 => !addressEquals(addr_1, addressToRemove)));
    }
  };
  const handleEditClick = () => {
    setIsEditing(true);
    setSearchTerm("");
    setTimeout(() => setOpen(true), 0);
  };
  const handleFocus = () => {
    setTimeout(() => setOpen(true), 0);
  };

  // Addresses currently rendered as clickable rows in the dropdown. Feeds the
  // "Use it anyway" decision: if the typed address is already in here, the
  // user should just click that verified row instead.
  const foundAddresses = useMemo(() => {
    const set = new Set<string>();
    for (const t_12 of allTokens) set.add(t_12.address.toLowerCase());
    for (const t_13 of workflowWalletTokens) set.add(t_13.address.toLowerCase());
    return [...set];
  }, [allTokens, workflowWalletTokens]);
  const showUnverifiedCommitButton = shouldShowUnverifiedCommitButton({
    searchTerm,
    isLoadingLookup: isLoading,
    foundAddresses
  });

  // User explicitly chooses to commit a typed address that didn't resolve to a
  // known token on the picker's chain(s). Checksums if possible. In multi mode
  // it appends to the array; in single mode it replaces the value and exits
  // the editing state, mirroring handleSelectToken.
  const handleUseUnverifiedAddress = () => {
    const raw = searchTerm.trim();
    // Non-strict: accept any case (see isValidAddress above for rationale).
    if (!raw.startsWith("0x") || raw.length !== 42 || !isAddress(raw, {
      strict: false
    })) return;
    // Lowercase before checksum-normalizing so mixed-case-with-bad-checksum
    // input doesn't throw. getAddress on a lowercase valid hex always produces
    // the canonical EIP-55 form.
    let checksum_1 = raw;
    try {
      checksum_1 = getAddress(raw.toLowerCase());
    } catch {
      // keep raw if normalization somehow fails (should not happen for valid hex)
    }
    // Cache the unverified token so the chip renders immediately after commit,
    // bypassing the isLoading gate in synthesizedUnverifiedToken. Without this,
    // SimDune re-fires on the new singleValue and the chip flashes empty.
    const unverifiedDisplay: SelectorDisplayToken = {
      chainId: numericChainId ?? 0,
      address: checksum_1,
      symbol: `${checksum_1.slice(0, 6)}…${checksum_1.slice(-4)}`,
      name: "Unverified token",
      metadata: {
        logoURI: null,
        verified: false,
        isNative: false
      }
    };
    setCachedSelectedTokens(prev_1 => ({
      ...prev_1,
      [normalizeAddress(checksum_1)]: unverifiedDisplay
    }));
    if (multiple) {
      if (!selectedAddresses.some(a_0 => addressEquals(a_0, checksum_1))) {
        onChange([...selectedAddresses, checksum_1]);
      }
      setSearchTerm("");
    } else {
      onChange(checksum_1);
      setSearchTerm("");
      setOpen(false);
      setIsEditing(false);
    }
  };

  // Filter tokens based on search and selected chain(s)
  const filteredTokens = allTokens.filter(token_0 => {
    // In multiple mode, exclude already-selected tokens
    if (multiple && selectedAddresses.some(addr_2 => addressEquals(addr_2, token_0.address))) {
      return false;
    }

    // Exclude native tokens when the field requires a contract-address token (e.g. ERC-20)
    if (excludeNative && isNativeToken(token_0)) return false;

    // Restrict to EVM chains when requested
    if (evmOnly && !isEvmChainId(token_0.chainId)) return false;

    // Filter by chainIds array if provided (takes precedence)
    if (hasValidChainIds) {
      if (!numericChainIds.includes(token_0.chainId)) return false;
    } else if (hasValidChainId && token_0.chainId !== numericChainId) {
      // Fall back to single chainId filter
      return false;
    }

    // Filter by search term
    if (!searchTerm) return true;
    const query = searchTerm.toLowerCase();
    if (!token_0) return false;
    return token_0.symbol?.toLowerCase().includes(query) || token_0.name?.toLowerCase().includes(query) || token_0.address?.toLowerCase().includes(query);
  });

  // Show native token first in popular section. Use the isNativeToken helper
  // (address + flag OR'd) so zero-address sentinels from data sources that
  // forgot to set `metadata.isNative` still sort correctly.
  const sortedTokens = [...filteredTokens].sort((a_1, b) => {
    if (isNativeToken(a_1)) return -1;
    if (isNativeToken(b)) return 1;
    if (a_1.metadata.verified && !b.metadata.verified) return -1;
    if (!a_1.metadata.verified && b.metadata.verified) return 1;
    return 0;
  });

  // Filter wallet tokens based on search and selected chain(s)
  const filteredWorkflowWalletTokens = workflowWalletTokens.filter(token_1 => {
    // In multiple mode, exclude already-selected tokens
    if (multiple && selectedAddresses.some(addr_3 => addressEquals(addr_3, token_1.address))) {
      return false;
    }

    // Exclude native tokens when the field requires a contract-address token (e.g. ERC-20)
    if (excludeNative && isNativeToken(token_1)) return false;

    // Restrict to EVM chains when requested
    if (evmOnly && !isEvmChainId(token_1.chainId)) return false;

    // Filter by chainIds array if provided (takes precedence)
    if (hasValidChainIds) {
      if (!numericChainIds.includes(token_1.chainId)) return false;
    } else if (hasValidChainId && token_1.chainId !== numericChainId) {
      // Fall back to single chainId filter
      return false;
    }

    // Filter by search term
    if (!searchTerm) return true;
    const query_0 = searchTerm.toLowerCase();
    if (!token_1) return false;
    return token_1.symbol?.toLowerCase().includes(query_0) || token_1.name?.toLowerCase().includes(query_0) || token_1.address?.toLowerCase().includes(query_0);
  }).sort((a_2, b_0) => (b_0.value_usd ?? 0) - (a_2.value_usd ?? 0));

  // Dedup popular list against wallet holdings — wallet section has balance + USD
  // value, so the richer row wins and the duplicate is hidden from discovery below.
  // Native sentinels are canonicalized ("native" vs 0x0…0) so ETH matches across
  // SimDune (wallet) and Relay (popular); non-native falls through to tokenInfoKey.
  const dedupKey = (chainId_0: number, address: string) => isNativeTokenAddress(address) ? `${chainId_0}:native` : tokenInfoKey(address, chainId_0);
  const walletTokenKeys = new Set(filteredWorkflowWalletTokens.map(t_14 => dedupKey(t_14.chainId, t_14.address)));
  const discoveryTokens = sortedTokens.filter(t_15 => !walletTokenKeys.has(dedupKey(t_15.chainId, t_15.address)));
  return <div className="space-y-2">
      {label && <div className="flex items-center justify-between gap-3">
          <label className="field-label block">
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
          {headerRight}
        </div>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
      {/* Multiple mode: Show selected tokens as chips */}
      {multiple && selectedTokens.length > 0 && <div className="flex flex-wrap gap-2">
          {selectedTokens.map(token_2 => <div key={token_2.address} className="border-ds-border-default dark:bg-ds-neutral-800 flex items-center gap-1.5 rounded-lg border bg-zinc-50 px-2 py-1 text-sm">
              <TokenSelectorIcon token={token_2} size={16} />
              <span className="text-ds-text-primary font-medium">{token_2.symbol}</span>
              <button type="button" onClick={() => handleRemoveToken(token_2.address)} className="text-ds-text-disabled hover:bg-ds-neutral-200 hover:text-ds-text-subtle dark:hover:bg-ds-neutral-700 ml-1 rounded-full p-0.5 transition-colors dark:hover:text-zinc-300">
                <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3l6 6M9 3l-6 6" />
                </svg>
              </button>
            </div>)}
        </div>}
      <Popover open={open} modal={popoverModal}>
        <PopoverAnchor asChild>
          <div className="relative">
            {!multiple && selectedToken && !isEditing ? (/* Display selected token info in the input (single mode only) */
          <div className={`bg-ds-surface-card hover:bg-ds-surface-hover flex min-h-[42px] w-full cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${selectedToken.metadata.verified ? "border-green-500 focus-visible:ring-green-500" : "border-yellow-500 focus-visible:ring-yellow-500"}`} onClick={handleEditClick}>
                <TokenSelectorIcon token={selectedToken} size={20} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <TokenSelectorTitle token={selectedToken} className="truncate" />
                    {!selectedToken.metadata.verified && <span className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400">
                        <IconAlertInfoOutline18 className="h-3.5 w-3.5" />
                        <span className="text-xs font-medium">Unverified</span>
                      </span>}
                  </div>
                  <div className="text-ds-text-subtle dark:text-ds-text-disabled flex items-center gap-2 text-xs">
                    <p className="truncate">
                      {selectedToken.name}
                      {chainInfoMap.get(selectedToken.chainId)?.name ? ` on ${chainInfoMap.get(selectedToken.chainId)?.name}` : ""}
                    </p>
                    {!isNativeToken(selectedToken) && <>
                        <span>·</span>
                        {(() => {
                    let checksumAddr = selectedToken.address;
                    try {
                      if (isAddress(selectedToken.address)) {
                        checksumAddr = getAddress(selectedToken.address);
                      }
                    } catch {
                      // Use original if conversion fails
                    }
                    return <p className="truncate font-mono">{formatAddress(checksumAddr, 4, 4)}</p>;
                  })()}
                      </>}
                  </div>
                </div>
                {selectedToken.metadata.verified && <IconCircleCheckOutline18 className="h-4 w-4 flex-shrink-0 text-green-500" />}
                {!required && <button type="button" aria-label="Clear selection" onClick={e => {
              e.stopPropagation();
              onChange("");
              setSearchTerm("");
              setIsEditing(false);
              setOpen(false);
            }} className="text-ds-text-disabled hover:bg-ds-neutral-200 hover:text-ds-text-subtle dark:hover:bg-ds-neutral-700 ml-1 rounded-full p-0.5 transition-colors dark:hover:text-zinc-300">
                    <IconXmarkOutline18 className="h-3 w-3" />
                  </button>}
              </div>) : (/* Regular text input when no token selected or editing */
          <div className="relative">
                <Input type="text" value={searchTerm} onChange={e_0 => handleInputChange(e_0.target.value)} onFocus={handleFocus} onBlur={() => {
              setTouched(true);
            }} placeholder={placeholder} className={`pr-9 ${showError ? "border-red-500 focus-visible:ring-red-500" : isValidAddress ? "border-green-500 focus-visible:ring-green-500" : ""}`} autoFocus={isEditing} />
                {/* Status icons */}
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  {isLoading && <PixelLoader size="sm" color="blue" />}
                  {!isLoading && showError && <IconAlertInfoOutline18 className="h-4 w-4 text-red-500" />}
                  {!isLoading && isValidAddress && <IconCircleCheckOutline18 className="h-4 w-4 text-green-500" />}
                </div>
              </div>)}
          </div>
        </PopoverAnchor>

        {/* Autocomplete Suggestions */}
        <PopoverContent className="border-ds-border-default bg-ds-surface-card w-[var(--radix-popover-trigger-width)] p-0 shadow-xl" align="start" onOpenAutoFocus={e_1 => e_1.preventDefault()} onInteractOutside={e_2 => {
        // In modal mode inside a Dialog, Radix fires onFocusOutside (with
        // preventDefault) when focus temporarily escapes during re-renders
        // (e.g. clicking a chain filter icon triggers state changes that
        // cause the focused button to briefly unmount). The modal popover
        // already prevents focus-outside dismissal — respect that by
        // checking defaultPrevented before closing.
        if (e_2.defaultPrevented) return;
        setOpen(false);
        setIsEditing(false);
        setChainFilterExpanded(false);
        // Discard uncommitted typed text — otherwise lookupTerm stays on
        // the typed address, simToken resolves to it, and the chip can
        // display a token that doesn't match the committed form value.
        setSearchTerm("");
      }}>
          {/* Chain filter inside dropdown */}
          {showChainFilter && !chainFilterExpanded && <div className="border-ds-border-default flex items-center gap-1 border-b px-2 py-1.5">
              <button type="button" onClick={() => {
            setSelectedFilterChainId(undefined);
          }} className={`flex-shrink-0 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${!selectedFilterChainId ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" : "text-ds-text-muted hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800"}`}>
                All
              </button>
              {chainFilterOptions.slice(0, 6).map(opt => <button key={`filter-${opt.chainId}`} type="button" title={opt.name} onClick={() => {
            setSelectedFilterChainId(opt.chainId);
            if (onChainIdChange) onChainIdChange(opt.chainId);
          }} className={`flex flex-shrink-0 items-center rounded-full p-1 transition-colors ${selectedFilterChainId === opt.chainId ? "bg-blue-100 ring-1 ring-blue-400 dark:bg-blue-900/40 dark:ring-blue-500" : "hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800"}`}>
                  <img src={opt.iconUrl} alt={opt.name} className="h-4 w-4 rounded-full" />
                </button>)}
              <button type="button" title="More chains" onClick={() => setChainFilterExpanded(true)} className="hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800 flex flex-shrink-0 items-center rounded-full p-1 transition-colors">
                <IconDotsOutline18 className="text-ds-text-disabled h-4 w-4" />
              </button>
            </div>}
          {/* Expanded chain search list */}
          {showChainFilter && chainFilterExpanded && <div>
              <div className="border-ds-border-default flex items-center gap-2 border-b px-3 py-2">
                <Input type="text" placeholder="Search chains..." value={chainSearchQuery} onChange={e_3 => handleChainSearchChange(e_3.target.value)} className="h-8 text-sm" autoFocus />
                <button type="button" onClick={() => {
              setChainFilterExpanded(false);
              handleChainSearchChange("");
            }} className="text-ds-text-disabled hover:text-ds-text-subtle flex-shrink-0 text-xs dark:hover:text-zinc-300">
                  Done
                </button>
              </div>
              <div className="max-h-[400px] overflow-y-auto">
                <div className="space-y-0.5">
                  <button type="button" onClick={() => {
                setSelectedFilterChainId(undefined);
                setChainFilterExpanded(false);
                handleChainSearchChange("");
              }} className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-blue-50 dark:hover:bg-blue-950/20 ${!selectedFilterChainId ? "bg-blue-50 dark:bg-blue-950/20" : ""}`}>
                    <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 flex h-8 w-8 items-center justify-center rounded-full">
                      <IconEarthOutline18 className="text-ds-text-muted h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="body-sm-semibold text-zinc-950 dark:text-white">All Chains</span>
                    </div>
                  </button>
                  {allChains.filter(c => !c.isTestnet && c.iconUrl).map(chain_0 => <button key={chain_0.chainId} type="button" onClick={() => {
                setSelectedFilterChainId(chain_0.chainId);
                if (onChainIdChange) onChainIdChange(chain_0.chainId);
                setChainFilterExpanded(false);
                handleChainSearchChange("");
              }} className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-blue-50 dark:hover:bg-blue-950/20 ${selectedFilterChainId === chain_0.chainId ? "bg-blue-50 dark:bg-blue-950/20" : ""}`}>
                        <img src={chain_0.iconUrl || ""} alt={chain_0.name} className="h-8 w-8 rounded-full" />
                        <div className="min-w-0 flex-1">
                          <span className="body-sm-semibold text-zinc-950 dark:text-white">{chain_0.name}</span>
                        </div>
                      </button>)}
                </div>
              </div>
            </div>}
          <div className={`max-h-[400px] overflow-y-auto ${chainFilterExpanded ? "hidden" : ""}`}>
            {showUnverifiedCommitButton && <div className="border-b border-yellow-300/60 bg-yellow-50/60 px-3 py-3 dark:border-yellow-700/40 dark:bg-yellow-950/20">
                <div className="flex items-start gap-2">
                  <IconAlertInfoOutline18 className="mt-0.5 h-4 w-4 shrink-0 text-yellow-700 dark:text-yellow-400" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-yellow-900 dark:text-yellow-200">Not a recognized token</p>
                    <p className="text-ds-text-subtle dark:text-ds-text-disabled text-2xs mt-0.5 break-all">
                      {formatAddress(searchTerm.trim(), 8, 6)}
                      {hasValidChainId && numericChainId && chainInfoMap.get(numericChainId)?.name ? ` — no token found on ${chainInfoMap.get(numericChainId)?.name}` : ""}
                    </p>
                    <button type="button" onClick={handleUseUnverifiedAddress} className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-yellow-500/60 bg-yellow-100/80 px-2.5 py-1 text-xs font-medium text-yellow-900 transition-colors hover:bg-yellow-200 dark:border-yellow-500/40 dark:bg-yellow-900/30 dark:text-yellow-100 dark:hover:bg-yellow-900/50">
                      Use this address anyway
                    </button>
                  </div>
                </div>
              </div>}
            {isLoading ? <div className="label-xs flex items-center justify-center py-4">
                <PixelLoader size="xs" className="mr-2" />
                Loading tokens...
              </div> :
          /* Empty-state gate uses the pre-dedup list: empty means Relay returned
             nothing, not that the user already holds every popular token. */
          sortedTokens.length === 0 ? <div className="space-y-3 py-4 text-center text-xs">
                {hasValidChainId && numericChainId ? <>
                    <div className="text-ds-text-secondary flex items-center justify-center gap-2">
                      {chainInfoMap.get(numericChainId)?.iconUrl && <img src={chainInfoMap.get(numericChainId)?.iconUrl || ""} alt="" loading="lazy" className="h-4 w-4 rounded-full" />}
                      <p>No tokens found on {chainInfoMap.get(numericChainId)?.name || `Chain ${numericChainId}`}</p>
                    </div>
                    <button type="button" onClick={() => {
                setSelectedFilterChainId(undefined);
              }} className="text-blue-600 underline decoration-dotted underline-offset-2 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                      Search on all chains
                    </button>
                  </> : <p className="text-ds-text-muted">No tokens found</p>}
                {searchTerm && !searchTerm.startsWith("0x") && <p className="text-ds-text-disabled">Try entering a token contract address (0x...)</p>}
              </div> : <div className="space-y-1">
                {/* Recently Used Tokens Section */}
                {(() => {
              if (searchTerm) return null;
              const filteredRecent = recentTokens.filter(rt_1 => {
                // Respect schema-level chain restrictions
                if (allowedChainIds && !allowedChainIds.includes(rt_1.chainId)) return false;
                if (excludeNative && isNativeTokenAddress(rt_1.address)) return false;
                if (evmOnly && !isEvmChainId(rt_1.chainId)) return false;
                if (hasValidChainIds && numericChainIds) return numericChainIds.includes(rt_1.chainId);
                if (hasValidChainId && numericChainId) return rt_1.chainId === numericChainId;
                return true;
              }).filter(rt_2 => {
                if (multiple) return !selectedAddresses.some(addr_4 => addressEquals(addr_4, rt_2.address));
                return true;
              });
              if (filteredRecent.length === 0) return null;
              return <div>
                      <div className="dark:bg-ds-neutral-900 flex items-center gap-1.5 bg-zinc-50 px-3 py-2">
                        <IconClockOutline18 className="text-ds-text-muted dark:text-ds-text-subtle h-3 w-3" />
                        <p className="text-ds-text-muted dark:text-ds-text-subtle text-xs font-semibold uppercase tracking-wide">
                          Recently Used
                        </p>
                      </div>
                      <div className="space-y-0.5">
                        {filteredRecent.map(rt_3 => {
                    const walletMatch = walletTokenByKey.get(tokenInfoKey(rt_3.address, rt_3.chainId));
                    const iconToken: SelectorDisplayToken = walletMatch ?? {
                      chainId: rt_3.chainId,
                      address: rt_3.address,
                      symbol: rt_3.symbol,
                      name: rt_3.name,
                      metadata: {
                        logoURI: rt_3.logoURI,
                        verified: true,
                        isNative: false
                      }
                    };
                    const displaySymbol = walletMatch?.symbol ?? rt_3.symbol;
                    const displayName = walletMatch?.name ?? rt_3.name;
                    return <button type="button" key={`recent-${rt_3.chainId}-${rt_3.address}`} onClick={() => handleSelectToken(rt_3.address, rt_3.chainId)} className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-blue-50 dark:hover:bg-blue-950/20">
                              <TokenSelectorIcon token={iconToken} size={32} />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="body-sm-semibold truncate text-zinc-950 dark:text-white">
                                    {displaySymbol}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                  <div className="text-ds-text-subtle dark:text-ds-text-disabled flex min-w-0 items-center gap-1 text-xs">
                                    <span className="truncate" title={displayName}>
                                      {displayName}
                                    </span>
                                    {!chainInfoMap.get(rt_3.chainId)?.iconUrl && chainInfoMap.get(rt_3.chainId)?.name && <span className="text-ds-text-disabled dark:text-ds-text-muted shrink-0">
                                        · {chainInfoMap.get(rt_3.chainId)!.name}
                                      </span>}
                                  </div>
                                  <p className="mono-xs flex-shrink-0">{formatAddress(rt_3.address, 4, 4)}</p>
                                </div>
                                {walletMatch && <TokenBalanceRow token={walletMatch} />}
                              </div>
                            </button>;
                  })}
                      </div>
                      <div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 my-2 h-px" />
                    </div>;
            })()}

                {/* Connected Wallet Section */}
                {filteredWorkflowWalletTokens.length > 0 && <div>
                    <div className="dark:bg-ds-neutral-900 flex items-center gap-1.5 bg-zinc-50 px-3 py-2">
                      <IconCryptoWallet className="text-ds-text-muted dark:text-ds-text-subtle h-3 w-3" />
                      <p className="text-ds-text-muted dark:text-ds-text-subtle text-xs font-semibold uppercase tracking-wide">
                        In your workflow wallet
                      </p>
                    </div>
                    <div className="space-y-0.5">
                      {filteredWorkflowWalletTokens.map((token_3, idx) => <button type="button" key={`workflow-${token_3.address}-${idx}`} onClick={() => handleSelectToken(token_3.address, token_3.chainId)} className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-blue-50 dark:hover:bg-blue-950/20">
                          <TokenSelectorIcon token={token_3} size={32} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <TokenSelectorTitle token={token_3} />
                              {isNativeToken(token_3) && <Badge variant="info" className="py-0">
                                  Native
                                </Badge>}
                              {token_3.metadata.verified && <IconCircleCheckOutline18 className="h-3 w-3 text-green-500" />}
                            </div>
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-ds-text-subtle dark:text-ds-text-disabled flex min-w-0 items-center gap-1 text-xs">
                                <span className="truncate" title={token_3.name}>
                                  {token_3.name}
                                </span>
                                {!chainInfoMap.get(token_3.chainId)?.iconUrl && chainInfoMap.get(token_3.chainId)?.name && <span className="text-ds-text-disabled dark:text-ds-text-muted shrink-0">
                                    · {chainInfoMap.get(token_3.chainId)!.name}
                                  </span>}
                              </div>
                              {!isNativeToken(token_3) && <p className="mono-xs flex-shrink-0">{formatAddress(token_3.address, 4, 4)}</p>}
                            </div>
                            <TokenBalanceRow token={token_3} />
                          </div>
                        </button>)}
                    </div>
                  </div>}

                {/* Popular Tokens Header */}
                {discoveryTokens.length > 0 && <>
                    {filteredWorkflowWalletTokens.length > 0 && <div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 my-2 h-px" />}
                    <div className="dark:bg-ds-neutral-900 mb-2 flex items-center gap-1.5 bg-zinc-50 px-3 py-2">
                      {hasValidChainId && numericChainId ? <>
                          {chainInfoMap.get(numericChainId)?.iconUrl && <img src={chainInfoMap.get(numericChainId)?.iconUrl || ""} alt="" loading="lazy" className="h-3 w-3 rounded-full" />}
                          <p className="text-ds-text-muted dark:text-ds-text-subtle text-xs font-semibold uppercase tracking-wide">
                            Tokens on {chainInfoMap.get(numericChainId)?.name || `Chain ${numericChainId}`}
                          </p>
                        </> : <>
                          <IconArrowTrendUpOutline18 className="h-3 w-3 text-green-600 dark:text-green-400" />
                          <p className="text-xs font-semibold uppercase tracking-wide text-green-700 dark:text-green-300">
                            Popular tokens
                          </p>
                        </>}
                    </div>
                    <div className="space-y-0.5">
                      {discoveryTokens.map((token_4, idx_0) => <button type="button" key={`${token_4.address}-${idx_0}`} onClick={() => handleSelectToken(token_4.address, token_4.chainId)} className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-blue-50 dark:hover:bg-blue-950/20">
                          <TokenSelectorIcon token={token_4} size={32} />
                          <div className="min-w-0 flex-1">
                            {/* Token Symbol + Badges */}
                            <div className="flex items-center gap-2">
                              <TokenSelectorTitle token={token_4} />
                              {isNativeToken(token_4) && <Badge variant="info">Native</Badge>}
                              {token_4.metadata.verified && <IconCircleCheckOutline18 className="h-3 w-3 text-green-500" />}
                            </div>

                            {/* Token Name + Chain icon + Address on same line */}
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-ds-text-subtle dark:text-ds-text-disabled flex min-w-0 items-center gap-1 text-xs">
                                <span className="truncate" title={token_4.name}>
                                  {token_4.name}
                                </span>
                                {!chainInfoMap.get(token_4.chainId)?.iconUrl && chainInfoMap.get(token_4.chainId)?.name && <span className="text-ds-text-disabled dark:text-ds-text-muted shrink-0">
                                    · {chainInfoMap.get(token_4.chainId)!.name}
                                  </span>}
                              </div>
                              {!isNativeToken(token_4) && <p className="mono-xs flex-shrink-0">{formatAddress(token_4.address, 4, 4)}</p>}
                            </div>
                          </div>
                        </button>)}
                    </div>
                  </>}
              </div>}
          </div>
        </PopoverContent>
      </Popover>
      {/* Error message */}
      {showError && required && <p className="micro-label text-red-600 dark:text-red-400">
          {multiple ? "At least one token is required" : "Token address is required"}
        </p>}
      {/* AnySpend deposit info box - only show in single mode when token is selected and we have valid chain/wallet */}
      {!multiple && selectedToken && !isEditing && workflowWalletAddress && hasValidChainId && showAnySpendFundingCta && <Button type="button" variant="link-brand" onClick={() => {
      setModalContentType({
        type: "anySpendDeposit",
        recipientAddress: workflowWalletAddress,
        destinationTokenAddress: selectedToken.address,
        destinationTokenChainId: numericChainId,
        actionLabel: anySpendActionLabel,
        allowDirectTransfer: true,
        showFiatOption: false
      });
      setModalOpen(true);
    }} className="micro-label text-ds-focus-brand hover:text-ds-focus-brand/80 h-auto gap-1.5 p-0">
            <span>{anySpendActionLabel}</span>
            <IconArrowRightOutline18 className="h-3 w-3" />
          </Button>}
    </div>;
}
export default TokenSelectorRelayMode;